import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import useFabricDetails from 'data-hooks/useFabricDetails';
import useFabricReviews from 'data-hooks/useFabricReviews';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import { GET_FABRIC_GALLERY_DATA } from 'mocks/queries';
import SwatchesFabricDetailSidebarUI from './index';

const SwatchesFabricDetailSidebar = ({
  isOpen,
  onClose,
  fabricId,
  galleryType,
  mobileHideHeader = false,
}) => {
  const zoomImgProps = useMemo(() => ({ w: 1800, h: 915 }), []);
  const jbImgProps = useMemo(() => ({ height: 300, width: 590 }), []);
  const {
    error,
    data: { selectedFabric },
  } = useFabricDetails(fabricId);

  const { data: fabricsGalleryData } = useSuspenseQuery(
    GET_FABRIC_GALLERY_DATA,
    {
      variables: { fromCache: true },
    }
  );

  const imgProps = useMemo(
    () => ({
      auto: 'format,compress',
      fit: 'fill',
      crop: 'entropy',
      h: 430,
      w: 875,
    }),
    []
  );

  const sliderImages = useMemo(() => {
    if (!selectedFabric || !fabricsGalleryData?.getFabricGalleryData) {
      return null;
    }

    const URL_PREFIX = 'https://joybird2.imgix.net/fabric-swatches/t1/';
    const itemData = fabricsGalleryData.getFabricGalleryData;
    const itemGalleryDataRaw = itemData[selectedFabric?.option_value_id];

    if (!itemGalleryDataRaw) {
      return null;
    }

    let itemGalleryData;
    try {
      itemGalleryData = JSON.parse(itemGalleryDataRaw);
    } catch (e) {
      return null;
    }

    const { img_prefix = '', img_suffixes = '' } = itemGalleryData;
    const img_suffixesList = img_suffixes.length
      ? img_suffixes.split(',')
      : null;

    if (!img_suffixesList) {
      return null;
    }

    const images = [];
    for (let i = 0; i < img_suffixesList.length; i++) {
      images.push({
        url: URL_PREFIX.concat(img_prefix, `-t1-${img_suffixesList[i]}.jpg`),
        isVideo: false,
        imgProps,
        zoomUrl: URL_PREFIX.concat(
          img_prefix,
          `-t1-${img_suffixesList[i]}.jpg`
        ),
        videoProps: {},
        className: '',
        zoomImgProps,
        jbImgProps,
      });
    }

    return images;
  }, [fabricsGalleryData, selectedFabric, imgProps, zoomImgProps, jbImgProps]);

  const selectedFabricWithSliderImages = useMemo(() => {
    if (!selectedFabric) {
      return null;
    }
    return {
      ...selectedFabric,
      sliderImages,
    };
  }, [selectedFabric, sliderImages]);

  const {
    data: { reviews, rating },
  } = useFabricReviews(fabricId);

  if (error) {
    return null;
  }

  return (
    <SwatchesFabricDetailSidebarUI
      isOpen={isOpen}
      onClose={onClose}
      selectedFabric={selectedFabricWithSliderImages}
      galleryType={galleryType}
      mobileHideHeader={mobileHideHeader}
      reviews={reviews}
      rating={rating}
    />
  );
};

SwatchesFabricDetailSidebar.propTypes = {
  fabricId: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  galleryType: PropTypes.string,
  mobileHideHeader: PropTypes.bool,
};

export default SwatchesFabricDetailSidebar;
